import type { Catalog, Range } from './catalog.js'
import { F } from './fields.js'
import type { EyeRx, OrderForm } from './order.js'
import { OFFERED_LENS_TYPES, signed } from './rx.js'

export interface FieldError { field: string; message: string }

export const MAX_NAME_LENGTH = 200
export const MAX_MEMBER_NUMBER_LENGTH = 50
export const MAX_PRISM_LENGTH = 20

// char.IsControl: Unicode category Cc
const CONTROL = /[\u0000-\u001f\u007f-\u009f]/

const hundredths = (v: number): number => Math.round(v * 100)

/** DecimalRange.Contains: in range and on the step grid (compared in hundredths to avoid float drift). */
function decimalContains(r: Range, v: number): boolean {
  if (!Number.isFinite(v)) return false
  const scaled = v * 100
  if (Math.abs(scaled - Math.round(scaled)) > 1e-9) return false // off the hundredths grid: C# decimal would reject
  const x = hundredths(v)
  const min = hundredths(r.min)
  return x >= min && x <= hundredths(r.max) && (x - min) % hundredths(r.step) === 0
}
const intContains = (r: Range, v: number): boolean => Number.isInteger(v) && v >= r.min && v <= r.max

const rangeText = (r: Range): string =>
  `between ${signed(r.min)} and ${signed(r.max)} in ${r.step.toFixed(2)} steps`

/** The spec's eight checks, plus the order date. Same order and wording as OrderValidator.cs. [] = valid. */
export function validateOrder(form: OrderForm, catalog: Catalog, todayEt: string): FieldError[] {
  const e: FieldError[] = []
  const add = (field: string, message: string): void => { e.push({ field, message }) }
  const r = catalog.ranges
  const f = form

  // Typed text goes into the email and the order record; a line break could forge email lines.
  const noControlChars = (trimmed: string | null | undefined, field: string, label: string): void => {
    if (trimmed != null && CONTROL.test(trimmed))
      add(field, `${label} can't contain line breaks or control characters.`)
  }
  const required = (value: string | null | undefined, max: number, field: string, label: string): void => {
    if (value == null || value.trim() === '') add(field, `${label} is required.`)
    else if (value.trim().length > max) add(field, `${label} must be ${max} characters or fewer.`)
    else noControlChars(value.trim(), field, label)
  }

  // Order date: required, and not a day that hasn't happened yet
  if (f.order_date == null) add(F.orderDate, 'Order date is required.')
  else if (f.order_date > todayEt) add(F.orderDate, "Order date can't be in the future.")

  // 1. Participant details
  required(f.participant_name, MAX_NAME_LENGTH, F.participantName, 'Participant name')
  required(f.member_number, MAX_MEMBER_NUMBER_LENGTH, F.memberNumber, 'Member number')
  if (f.center_id == null || f.center_id.trim() === '') add(F.center, 'Choose a center.')
  else if (!catalog.centers.some((c) => c.id === f.center_id)) add(F.center, 'Choose a center from the list.')
  if (f.birth_year == null) add(F.birthYear, 'Birth year is required.')
  else if (!intContains(r.birthYear, f.birth_year))
    add(F.birthYear, `Birth year must be between ${r.birthYear.min} and ${r.birthYear.max}.`)
  if (f.pupillary_distance_mm == null) add(F.pupillaryDistance, 'Pupillary distance is required.')
  else if (!intContains(r.pupillaryDistanceMm, f.pupillary_distance_mm))
    add(F.pupillaryDistance,
      `Pupillary distance must be between ${r.pupillaryDistanceMm.min} and ${r.pupillaryDistanceMm.max} mm.`)

  // 2. EMR upload confirmed
  if (f.rx_uploaded_to_emr == null) add(F.rxUploadedToEmr, 'Say whether the prescription is uploaded to the EMR.')
  else if (f.rx_uploaded_to_emr === false)
    add(F.rxUploadedToEmr, 'Upload the prescription to the EMR before ordering.')

  // 3. Frames and colors
  const selected = Object.entries(f.frames)
  if (selected.length === 0) add(F.frames, 'Select at least one frame.')
  for (const [id, color] of selected) {
    const frame = catalog.frames.find((x) => x.id === id)
    if (!frame) {
      add(F.frames, 'A selected frame is no longer offered.')
      continue
    }
    if (color == null || color === '') add(F.frame(id), `Pick a color for ${frame.name} #${frame.productNumber}.`)
    else if (!frame.colors.includes(color))
      add(F.frame(id), `Pick one of the listed colors for ${frame.name} #${frame.productNumber}.`)
  }
  if (f.clip_on_id != null && !catalog.clipOns.some((c) => c.id === f.clip_on_id))
    add(F.clipOn, 'Choose a clip-on from the list, or none.')

  // 4, 5, 7. Per-eye prescription
  const eye = (rx: EyeRx, which: 'Od' | 'Os', label: string): void => {
    if (rx.sphere == null) add(F.eye(which, 'Sphere'), `${label}: sphere is required.`)
    else if (!decimalContains(r.sphere, rx.sphere))
      add(F.eye(which, 'Sphere'), `${label}: sphere must be ${rangeText(r.sphere)}.`)

    if (rx.cylinder == null) add(F.eye(which, 'Cylinder'), `${label}: cylinder is required.`)
    else if (!decimalContains(r.cylinder, rx.cylinder))
      add(F.eye(which, 'Cylinder'), `${label}: cylinder must be ${rangeText(r.cylinder)}.`)

    if (rx.cylinder != null && rx.cylinder !== 0 && rx.axis == null)
      add(F.eye(which, 'Axis'), `${label}: axis is required when cylinder isn't 0.`)
    if (rx.axis != null && !intContains(r.axis, rx.axis))
      add(F.eye(which, 'Axis'), `${label}: axis must be between ${r.axis.min} and ${r.axis.max}.`)

    const prism = rx.prism?.trim()
    const base = rx.base?.trim()
    noControlChars(prism, F.eye(which, 'Prism'), `${label}: prism`)
    noControlChars(base, F.eye(which, 'Base'), `${label}: base`)
    if (prism) {
      if (prism.length > MAX_PRISM_LENGTH)
        add(F.eye(which, 'Prism'), `${label}: prism must be ${MAX_PRISM_LENGTH} characters or fewer.`)
      if (!base) add(F.eye(which, 'Base'), `${label}: base is required when prism is entered.`)
    }
    if (base !== undefined && base.length > MAX_PRISM_LENGTH)
      add(F.eye(which, 'Base'), `${label}: base must be ${MAX_PRISM_LENGTH} characters or fewer.`)
  }
  eye(f.od, 'Od', 'Right eye (OD)')
  eye(f.os, 'Os', 'Left eye (OS)')

  // 6. ADD in range, and it needs a lens type
  if (f.add != null && !decimalContains(r.add, f.add)) add(F.add, `ADD must be ${rangeText(r.add)}.`)
  if (f.lens_type != null && !(OFFERED_LENS_TYPES as readonly string[]).includes(f.lens_type))
    add(F.lensType, 'Reading only is no longer offered; choose single vision, progressive or bifocal.')
  else if (f.add != null && f.lens_type == null)
    add(F.lensType, 'ADD is filled in, so choose progressive or bifocal.')

  // 8. Cylinder sign agrees across eyes
  const odCyl = f.od.cylinder
  const osCyl = f.os.cylinder
  if (odCyl != null && osCyl != null && odCyl !== 0 && osCyl !== 0 && Math.sign(odCyl) !== Math.sign(osCyl))
    add(F.eye('Os', 'Cylinder'), 'Cylinder must be negative for both eyes or positive for both, not one of each.')

  return e
}
